import { AnalyticsEvent, ConsentSnapshot, EventItem, EventPayload, isCriticalEvent } from '../types'
import { Transport } from './Transport'
import { getServerEndpoint, SERVER_BATCH_MAX, SERVER_FLUSH_INTERVAL_MS } from '../config'
import { logger } from '../util/logger'
import { incCounter } from '../util/debugCounters'

// sendBeacon has a practical ceiling of ~64 KB. Above this we fall back to
// fetch with keepalive: true, which handles larger payloads on most browsers.
const BEACON_MAX_BYTES = 64 * 1024

// Offline queue is bounded to prevent unbounded sessionStorage growth.
// Events are dropped from the front (oldest) when the cap is reached.
const OFFLINE_QUEUE_KEY = 'hh.analytics.offline_queue'
const OFFLINE_QUEUE_MAX = 100

interface ServerEnvelope {
  event_name: string
  event_id: string
  event_time?: number
  event_source_url?: string
  user?: {
    external_id?: string
    email?: string
    phone?: string
    fbp?: string
    fbc?: string
    ga_client_id?: string
  }
  custom_data?: {
    items?: ServerItem[]
    value?: number
    currency?: string
    transaction_id?: string
    search_term?: string
    method?: string
    payment_type?: string
    list_id?: string
    list_name?: string
  }
  consent?: ConsentSnapshot
}

interface ServerItem {
  id: string
  name: string
  category?: string
  variant?: string
  price: number
  quantity: number
  discount?: number
}

function mapItems(items: EventItem[]): ServerItem[] {
  return items.map(i => ({
    id: i.id,
    name: i.name,
    category: i.category,
    variant: i.variant,
    price: i.price,
    quantity: i.quantity,
    discount: i.discount,
  }))
}

function mapPayload(
  payload: EventPayload,
): ServerEnvelope['custom_data'] {
  const cd: ServerEnvelope['custom_data'] = {}
  if (payload.items?.length) cd.items = mapItems(payload.items)
  if (payload.value !== undefined) cd.value = payload.value
  if (payload.currency) cd.currency = payload.currency
  if (payload.transaction_id) cd.transaction_id = payload.transaction_id
  if (payload.search_term) cd.search_term = payload.search_term
  if (payload.method) cd.method = payload.method
  if (payload.payment_type) cd.payment_type = payload.payment_type
  if (payload.list_id) cd.list_id = payload.list_id
  if (payload.list_name) cd.list_name = payload.list_name
  return Object.keys(cd).length ? cd : undefined
}

function toEnvelope(event: AnalyticsEvent): ServerEnvelope {
  const env: ServerEnvelope = {
    event_name: event.name,
    event_id: event.event_id,
    event_time: event.event_time,
    event_source_url: event.event_source_url || undefined,
    consent: event.consent,
  }

  const user: ServerEnvelope['user'] = {}
  if (event.external_id) user.external_id = event.external_id
  if (event.email) user.email = event.email
  if (event.phone) user.phone = event.phone
  if (event.fbp) user.fbp = event.fbp
  if (event.fbc) user.fbc = event.fbc
  if (event.ga_client_id) user.ga_client_id = event.ga_client_id
  if (Object.keys(user).length) env.user = user

  const custom = mapPayload(event.payload)
  if (custom) env.custom_data = custom

  return env
}

function getAuthHeaders(): Record<string, string> {
  const accessToken = localStorage.getItem('accessToken')
  const idToken = localStorage.getItem('idToken')
  if (accessToken?.trim()) return { Authorization: `Bearer ${accessToken}` }
  if (idToken?.trim()) return { Authorization: `Bearer ${idToken}` }
  return {}
}

// ---------------------------------------------------------------------------
// Offline queue helpers (sessionStorage — intentionally not localStorage so
// undelivered events don't leak across browser sessions).
// ---------------------------------------------------------------------------

function readOfflineQueue(): AnalyticsEvent[] {
  try {
    const raw = sessionStorage.getItem(OFFLINE_QUEUE_KEY)
    return raw ? (JSON.parse(raw) as AnalyticsEvent[]) : []
  } catch {
    return []
  }
}

function writeOfflineQueue(events: AnalyticsEvent[]): void {
  try {
    sessionStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(events))
  } catch {
    // sessionStorage unavailable or quota exceeded — events are lost, not critical
  }
}

function enqueueOffline(event: AnalyticsEvent): void {
  const queue = readOfflineQueue()
  if (queue.length >= OFFLINE_QUEUE_MAX) {
    queue.shift() // drop oldest to make room
  }
  queue.push(event)
  writeOfflineQueue(queue)
}

// ---------------------------------------------------------------------------

export class ServerTransport implements Transport {
  readonly name = 'ServerTransport'

  private readonly url: string = getServerEndpoint()
  private readonly buffer: AnalyticsEvent[] = []
  private timer: ReturnType<typeof setTimeout> | null = null

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.onPageHide)
      window.addEventListener('online', this.onOnline)
    }
  }

  send(event: AnalyticsEvent): void {
    // While offline, buffer to sessionStorage and return immediately.
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      enqueueOffline(event)
      incCounter('queued')
      return
    }

    if (isCriticalEvent(event.name)) {
      this.post(event)
      return
    }

    this.buffer.push(event)

    if (this.buffer.length >= SERVER_BATCH_MAX) {
      this.flush()
      return
    }

    if (this.timer === null) {
      this.timer = setTimeout(() => {
        this.flush()
      }, SERVER_FLUSH_INTERVAL_MS)
    }
  }

  flush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer)
      this.timer = null
    }
    const events = this.buffer.splice(0)
    events.forEach(e => this.post(e))
  }

  private post(event: AnalyticsEvent): void {
    const envelope = toEnvelope(event)
    const body = JSON.stringify(envelope)

    // Fire-and-forget. No retries: vendors deduplicate by event_id, so a lost
    // event is a lost event. Retrying without server-side idempotency guarantees
    // would risk phantom duplicates.
    fetch(this.url, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body,
    }).then(res => {
      if (res.ok) {
        incCounter('dispatched')
      } else {
        incCounter('failed')
        logger.debug('ServerTransport HTTP error:', res.status)
      }
    }).catch(err => {
      incCounter('failed')
      logger.debug('ServerTransport fetch error:', err)
    })
  }

  private beaconFlush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer)
      this.timer = null
    }
    const events = this.buffer.splice(0)
    events.forEach(e => {
      try {
        const envelope = toEnvelope(e)
        const body = JSON.stringify(envelope)
        const blob = new Blob([body], { type: 'application/json' })

        // sendBeacon has a practical ceiling of ~64 KB. Fall back to fetch with
        // keepalive: true for larger payloads (rare for a single event but possible).
        if (navigator.sendBeacon && blob.size <= BEACON_MAX_BYTES) {
          navigator.sendBeacon(this.url, blob)
        } else {
          fetch(this.url, {
            method: 'POST',
            credentials: 'include',
            keepalive: true,
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body,
          }).catch(() => {})
        }
      } catch (err) {
        logger.debug('ServerTransport beaconFlush error:', err)
      }
    })
  }

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.beaconFlush()
    }
  }

  private onPageHide = (): void => {
    this.beaconFlush()
  }

  // Flush the sessionStorage offline queue when connectivity is restored.
  private onOnline = (): void => {
    const queue = readOfflineQueue()
    if (queue.length === 0) return
    writeOfflineQueue([]) // clear before posting to avoid double-delivery on rapid offline/online
    queue.forEach(e => this.post(e))
  }
}
